import React, { useEffect, useState } from "react";
import { Button, Modal, Table, Form } from "react-bootstrap";
import { BsSearch, BsPlus, BsTrash, BsPencil, BsTelephone } from "react-icons/bs";
import axios from "axios";
import ReactPaginate from "react-paginate";
import { AiFillDelete } from "react-icons/ai";
import { BiSolidEdit } from "react-icons/bi";

function DeliveryRate() {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPickupPointsModal, setShowPickupPointsModal] = useState(false);
    const [showAddPickupPointModal, setShowAddPickupPointModal] = useState(false);
    const [showEditPickupPointModal, setShowEditPickupPointModal] = useState(false);

    // Form states
    const [hubId, setHubId] = useState("");
    const [hubName, setHubName] = useState("");
    const [doorDeliveryAvailable, setDoorDeliveryAvailable] = useState(false);
    const [doorDeliveryRate, setDoorDeliveryRate] = useState("");
    const [gateDeliveryAvailable, setGateDeliveryAvailable] = useState(false);
    const [pickupPoints, setPickupPoints] = useState([]);
    const [status, setStatus] = useState("Normal");

    // Pickup point form states
    const [pickupPointName, setPickupPointName] = useState("");
    const [pickupPointLocation, setPickupPointLocation] = useState("");
    const [pickupPointContactNumber, setPickupPointContactNumber] = useState("");
    const [pickupPointDescription, setPickupPointDescription] = useState("");
    const [editingPickupPoint, setEditingPickupPoint] = useState(null);

    // Delivery slots management
    const [deliverySlots, setDeliverySlots] = useState([]);
    const [currentSession, setCurrentSession] = useState("Lunch");
    const [timeSlots, setTimeSlots] = useState([{ startTime: "", endTime: "" }]);

    // Data states
    const [deliveryRates, setDeliveryRates] = useState([]);
    const [filteredRates, setFilteredRates] = useState([]);
    const [hubs, setHubs] = useState([]);
    const [selectedRate, setSelectedRate] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination
    const [pageNumber, setPageNumber] = useState(0);
    const ratesPerPage = 6;
    const pagesVisited = pageNumber * ratesPerPage;
    const pageCount = Math.ceil((filteredRates?.length || 0) / ratesPerPage);

    const sessionOptions = ["Breakfast", "Lunch", "Dinner"];

    // The API may return either a bare array or { success, data: [...] }
    const toArray = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    };

    // Fetch hubs
    const getHubs = async () => {
        try {
            const res = await axios.get("https://dailydish.in/api/Hub/hubs");
            setHubs(toArray(res.data));
        } catch (error) {
            console.error("Error fetching hubs:", error);
            setHubs([]);
        }
    };

    // Fetch delivery rates
    const getDeliveryRates = async () => {
        try {
            const res = await axios.get("https://dailydish.in/api/deliveryrate/all");
            const rates = toArray(res.data);
            setDeliveryRates(rates);
            setFilteredRates(rates);
        } catch (error) {
            console.error("Error fetching delivery rates:", error);
            setDeliveryRates([]);
            setFilteredRates([]);
        }
    };

    // Add delivery rate
    const addDeliveryRate = async () => {
        try {
            const selectedHub = hubs.find(h => h._id === hubId);

            const data = {
                hubId: hubId,
                hubName: selectedHub?.hubName || hubName,
                doorDeliveryAvailable: doorDeliveryAvailable,
                doorDeliveryRate: doorDeliveryAvailable ? Number(doorDeliveryRate) : null,
                gateDeliveryAvailable: gateDeliveryAvailable,
                pickupPoints: gateDeliveryAvailable ? pickupPoints : [],
                status: status
            };

            const config = {
                url: "deliveryrate/add",
                method: "post",
                baseURL: "https://dailydish.in/api",
                headers: { "Content-Type": "application/json" },
                data: data
            };

            const res = await axios(config);
            if (res.status === 200) {
                alert("Delivery Rate Added Successfully");
                handleCloseAdd();
                getDeliveryRates();
            }
        } catch (error) {
            console.error("Error adding delivery rate:", error);
            alert(error.response?.data?.message || "Error adding delivery rate");
        }
    };

    // Update delivery rate
    const updateDeliveryRate = async () => {
        try {
            const data = {};

            if (doorDeliveryAvailable !== selectedRate.doorDeliveryAvailable) {
                data.doorDeliveryAvailable = doorDeliveryAvailable;
            }

            if (doorDeliveryRate !== selectedRate.doorDeliveryRate) {
                data.doorDeliveryRate = doorDeliveryAvailable ? Number(doorDeliveryRate) : null;
            }

            if (gateDeliveryAvailable !== selectedRate.gateDeliveryAvailable) {
                data.gateDeliveryAvailable = gateDeliveryAvailable;
            }

            if (gateDeliveryAvailable && JSON.stringify(pickupPoints) !== JSON.stringify(selectedRate.pickupPoints)) {
                data.pickupPoints = pickupPoints;
            }

            if (status !== selectedRate.status) {
                data.status = status;
            }

            const config = {
                url: `deliveryrate/update/${selectedRate._id}`,
                method: "put",
                baseURL: "https://dailydish.in/api",
                headers: { "Content-Type": "application/json" },
                data: data
            };

            const res = await axios(config);
            if (res.status === 200) {
                alert("Delivery Rate Updated Successfully");
                handleCloseEdit();
                getDeliveryRates();
            }
        } catch (error) {
            console.error("Error updating delivery rate:", error);
            alert(error.response?.data?.message || "Error updating delivery rate");
        }
    };

    // Delete delivery rate
    const deleteDeliveryRate = async () => {
        try {
            const res = await axios.delete(
                `https://dailydish.in/api/deliveryrate/delete/${selectedRate._id}`
            );

            if (res.status === 200) {
                alert("Delivery Rate Deleted Successfully");
                handleCloseDelete();
                getDeliveryRates();
            }
        } catch (error) {
            console.error("Error deleting delivery rate:", error);
            alert(error.message);
        }
    };

    // Add pickup point
    const addPickupPoint = async () => {
        try {
            const data = {
                name: pickupPointName,
                location: pickupPointLocation,
                contactNumber: pickupPointContactNumber || null,
                deliverySlots: deliverySlots,
                description: pickupPointDescription
            };

            const config = {
                url: `deliveryrate/pickup-point/${selectedRate._id}/add`,
                method: "post",
                baseURL: "https://dailydish.in/api",
                headers: { "Content-Type": "application/json" },
                data: data
            };

            const res = await axios(config);
            if (res.status === 200) {
                alert("Pickup Point Added Successfully");
                setShowAddPickupPointModal(false);
                resetPickupPointForm();
                getDeliveryRates();
                if (selectedRate) {
                    const updatedRate = res.data.data;
                    setSelectedRate(updatedRate);
                    setPickupPoints(updatedRate.pickupPoints || []);
                }
            }
        } catch (error) {
            console.error("Error adding pickup point:", error);
            alert(error.response?.data?.message || "Error adding pickup point");
        }
    };

    // Edit pickup point
    const editPickupPoint = async () => {
        try {
            const data = {
                name: pickupPointName,
                location: pickupPointLocation,
                contactNumber: pickupPointContactNumber || null,
                deliverySlots: deliverySlots,
                description: pickupPointDescription
            };

            const config = {
                url: `deliveryrate/pickup-point/${selectedRate._id}/update/${editingPickupPoint._id}`,
                method: "put",
                baseURL: "https://dailydish.in/api",
                headers: { "Content-Type": "application/json" },
                data: data
            };

            const res = await axios(config);
            if (res.status === 200) {
                alert("Pickup Point Updated Successfully");
                setShowEditPickupPointModal(false);
                resetPickupPointForm();
                getDeliveryRates();
                if (selectedRate) {
                    const updatedRate = res.data.data;
                    setSelectedRate(updatedRate);
                    setPickupPoints(updatedRate.pickupPoints || []);
                }
            }
        } catch (error) {
            console.error("Error updating pickup point:", error);
            alert(error.response?.data?.message || "Error updating pickup point");
        }
    };

    // Remove pickup point
    const removePickupPoint = async (pickupPointId) => {
        if (window.confirm("Are you sure you want to remove this pickup point?")) {
            try {
                const res = await axios.delete(
                    `https://dailydish.in/api/deliveryrate/pickup-point/${selectedRate._id}/remove/${pickupPointId}`
                );

                if (res.status === 200) {
                    alert("Pickup Point Removed Successfully");
                    const updatedRate = res.data.data;
                    setSelectedRate(updatedRate);
                    setPickupPoints(updatedRate.pickupPoints || []);
                    getDeliveryRates();
                }
            } catch (error) {
                console.error("Error removing pickup point:", error);
                alert(error.response?.data?.message || "Error removing pickup point");
            }
        }
    };

    // Add time slot
    const addTimeSlot = () => {
        setTimeSlots([...timeSlots, { startTime: "", endTime: "" }]);
    };

    // Remove time slot
    const removeTimeSlot = (index) => {
        const updatedSlots = timeSlots.filter((_, i) => i !== index);
        setTimeSlots(updatedSlots);
    };

    // Update time slot
    const updateTimeSlot = (index, field, value) => {
        const updatedSlots = [...timeSlots];
        updatedSlots[index][field] = value;
        setTimeSlots(updatedSlots);
    };

    // Add delivery slot session
    const addDeliverySlotSession = () => {
        const validTimeSlots = timeSlots.filter(ts => ts.startTime && ts.endTime);
        if (validTimeSlots.length === 0) {
            alert("Please add at least one valid time slot");
            return;
        }

        // Check if session already exists
        const sessionExists = deliverySlots.some(slot => slot.session === currentSession);
        if (sessionExists) {
            alert(`${currentSession} session already exists. Please remove existing session first or edit it.`);
            return;
        }

        const newDeliverySlot = {
            session: currentSession,
            timeSlots: validTimeSlots
        };

        setDeliverySlots([...deliverySlots, newDeliverySlot]);
        setCurrentSession("Lunch");
        setTimeSlots([{ startTime: "", endTime: "" }]);
    };

    // Remove delivery slot session
    const removeDeliverySlotSession = (index) => {
        const updatedSlots = deliverySlots.filter((_, i) => i !== index);
        setDeliverySlots(updatedSlots);
    };

    // Handle hub selection
    const handleHubChange = (e) => {
        const selectedHubId = e.target.value;
        setHubId(selectedHubId);

        const selectedHub = hubs.find(h => h._id === selectedHubId);
        if (selectedHub) {
            setHubName(selectedHub.hubName);
        }
    };

    // Get hub display name with ID
    const getHubDisplayName = (hub) => {
        return `${hub.hubName} (${hub.hubId || hub._id})`;
    };

    // Format delivery display
    const formatDoorDelivery = (available, rate) => {
        if (available) {
            return `â‚¹${rate}`;
        }
        return "Not Available";
    };

    const formatGateDelivery = (available, points) => {
        if (available) {
            return `${points?.length || 0} Pickup Points (Free)`;
        }
        return "Not Available";
    };

    // Search filter
    useEffect(() => {
        const filtered = (deliveryRates || []).filter(rate =>
            rate.hubName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rate.hubId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rate.status?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredRates(filtered);
        setPageNumber(0);
    }, [searchTerm, deliveryRates]);

    // Modal handlers
    const handleShowAdd = () => {
        setHubId("");
        setHubName("");
        setDoorDeliveryAvailable(false);
        setDoorDeliveryRate("");
        setGateDeliveryAvailable(false);
        setPickupPoints([]);
        setStatus("Normal");
        setShowAddModal(true);
    };

    const handleCloseAdd = () => {
        setShowAddModal(false);
        resetForm();
    };

    const handleShowEdit = (rate) => {
        setSelectedRate(rate);
        setHubId(rate.hubId);
        setHubName(rate.hubName);
        setDoorDeliveryAvailable(rate.doorDeliveryAvailable);
        setDoorDeliveryRate(rate.doorDeliveryRate || "");
        setGateDeliveryAvailable(rate.gateDeliveryAvailable);
        setPickupPoints(rate.pickupPoints || []);
        setStatus(rate.status);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        resetForm();
    };

    const handleShowDelete = (rate) => {
        setSelectedRate(rate);
        setShowDeleteModal(true);
    };

    const handleCloseDelete = () => {
        setShowDeleteModal(false);
        setSelectedRate(null);
    };

    const handleShowPickupPoints = (rate) => {
        setSelectedRate(rate);
        setPickupPoints(rate.pickupPoints || []);
        setShowPickupPointsModal(true);
    };

    const handleClosePickupPoints = () => {
        setShowPickupPointsModal(false);
        setSelectedRate(null);
        setPickupPoints([]);
    };

    const handleShowAddPickupPoint = () => {
        resetPickupPointForm();
        setDeliverySlots([]);
        setCurrentSession("Lunch");
        setTimeSlots([{ startTime: "", endTime: "" }]);
        setShowAddPickupPointModal(true);
    };

    const handleCloseAddPickupPoint = () => {
        setShowAddPickupPointModal(false);
        resetPickupPointForm();
    };

    const handleShowEditPickupPoint = (point) => {
        setEditingPickupPoint(point);
        setPickupPointName(point.name);
        setPickupPointLocation(point.location);
        setPickupPointContactNumber(point.contactNumber || "");
        setPickupPointDescription(point.description || "");
        setDeliverySlots(point.deliverySlots || []);
        setShowEditPickupPointModal(true);
    };

    const handleCloseEditPickupPoint = () => {
        setShowEditPickupPointModal(false);
        resetPickupPointForm();
        setEditingPickupPoint(null);
    };

    const resetForm = () => {
        setHubId("");
        setHubName("");
        setDoorDeliveryAvailable(false);
        setDoorDeliveryRate("");
        setGateDeliveryAvailable(false);
        setPickupPoints([]);
        setStatus("Normal");
        setSelectedRate(null);
    };

    const resetPickupPointForm = () => {
        setPickupPointName("");
        setPickupPointLocation("");
        setPickupPointContactNumber("");
        setPickupPointDescription("");
        setDeliverySlots([]);
        setEditingPickupPoint(null);
    };

    // Pagination
    const changePage = ({ selected }) => {
        setPageNumber(selected);
    };

    useEffect(() => {
        getHubs();
        getDeliveryRates();
    }, []);

    const statusOptions = ["Normal", "Employee"];

    return (
        <div>
            <h2 className="header-c">Delivery Rate</h2>
            <div className="customerhead p-2">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="col-lg-4 d-flex justify-content-center">
                        <div className="input-group">
                            <span className="input-group-text" id="basic-addon1">
                                <BsSearch />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by Hub, Status..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <Button variant="success" onClick={handleShowAdd}>
                        + ADD Delivery Rate
                    </Button>
                </div>

                <div className="mb-3">
                    <Table responsive bordered>
                        <thead>
                            <tr>
                                <th>Sl. No</th>
                                <th>Hub ID</th>
                                <th>Hub Name</th>
                                <th>Door Delivery</th>
                                <th>Gate Delivery</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRates
                                ?.slice(pagesVisited, pagesVisited + ratesPerPage)
                                ?.map((rate, i) => (
                                    <tr key={rate._id}>
                                        <td style={{ paddingTop: "20px" }}>
                                            {i + 1 + ratesPerPage * pageNumber}
                                        </td>
                                        <td style={{ paddingTop: "20px" }}>
                                            {rate.hubId ? (rate.hubId.length > 10 ? rate.hubId.slice(-6) : rate.hubId) : 'N/A'}
                                        </td>
                                        <td style={{ paddingTop: "20px" }}>{rate.hubName}</td>
                                        <td style={{ paddingTop: "20px" }}>
                                            <span className={`badge ${rate.doorDeliveryAvailable ? 'bg-success' : 'bg-secondary'}`}>
                                                {formatDoorDelivery(rate.doorDeliveryAvailable, rate.doorDeliveryRate)}
                                            </span>
                                        </td>
                                        <td style={{ paddingTop: "20px" }}>
                                            <Button
                                                variant="link"
                                                className={`badge ${rate.gateDeliveryAvailable ? 'bg-info' : 'bg-secondary'} text-decoration-none`}
                                                onClick={() => rate.gateDeliveryAvailable && handleShowPickupPoints(rate)}
                                                style={{ cursor: rate.gateDeliveryAvailable ? 'pointer' : 'default' }}
                                            >
                                                {formatGateDelivery(rate.gateDeliveryAvailable, rate.pickupPoints)}
                                            </Button>
                                        </td>
                                        <td style={{ paddingTop: "20px" }}>
                                            <span className={`badge ${rate.status === 'Employee' ? 'bg-info' : 'bg-secondary'}`}>
                                                {rate.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: "flex", gap: "20px", justifyContent: "center" }}>
                                                <div>
                                                    <BiSolidEdit
                                                        className="text-success"
                                                        style={{ cursor: "pointer", fontSize: "20px" }}
                                                        onClick={() => handleShowEdit(rate)}
                                                    />
                                                </div>
                                                <div>
                                                    <AiFillDelete
                                                        className="text-danger"
                                                        style={{ cursor: "pointer", fontSize: "20px" }}
                                                        onClick={() => handleShowDelete(rate)}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </Table>

                    <div style={{ display: "flex" }} className="reactPagination">
                        <p style={{ width: "100%", marginTop: "20px" }}>
                            Total Count: {filteredRates?.length}
                        </p>
                        <ReactPaginate
                            previousLabel={"Back"}
                            nextLabel={"Next"}
                            pageCount={pageCount}
                            onPageChange={changePage}
                            containerClassName={"paginationBttns"}
                            previousLinkClassName={"previousBttn"}
                            nextLinkClassName={"nextBttn"}
                            disabledClassName={"paginationDisabled"}
                            activeClassName={"paginationActive"}
                        />
                    </div>
                </div>

                {/* Add Modal */}
                <Modal show={showAddModal} onHide={handleCloseAdd} style={{ zIndex: "99999" }} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Add Delivery Rate</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Hub</Form.Label>
                                    <Form.Select value={hubId} onChange={handleHubChange}>
                                        <option value="">Select Hub</option>
                                        {hubs.map((hub) => (
                                            <option key={hub._id} value={hub._id}>
                                                {getHubDisplayName(hub)}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="do-sear">
                                    <Form.Label>Door Delivery Available</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="door-delivery-switch"
                                        label={doorDeliveryAvailable ? "Yes" : "No"}
                                        checked={doorDeliveryAvailable}
                                        onChange={(e) => {
                                            setDoorDeliveryAvailable(e.target.checked);
                                            if (!e.target.checked) {
                                                setDoorDeliveryRate("");
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {doorDeliveryAvailable && (
                                <div className="row">
                                    <div className="do-sear mt-2">
                                        <Form.Label>Door Delivery Rate (â‚¹)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Enter Door Delivery Rate"
                                            value={doorDeliveryRate}
                                            onChange={(e) => setDoorDeliveryRate(e.target.value)}
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                        <small className="text-muted">Rate charged for door delivery</small>
                                    </div>
                                </div>
                            )}

                            <div className="row mt-3">
                                <div className="do-sear">
                                    <Form.Label>Gate Delivery Available (Free)</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="gate-delivery-switch"
                                        label={gateDeliveryAvailable ? "Yes - Add Pickup Points" : "No"}
                                        checked={gateDeliveryAvailable}
                                        onChange={(e) => {
                                            setGateDeliveryAvailable(e.target.checked);
                                            if (!e.target.checked) {
                                                setPickupPoints([]);
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        {statusOptions.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="d-flex">
                            <Button className="mx-2 modal-close-btn" variant="" onClick={handleCloseAdd}>
                                Close
                            </Button>
                            <Button className="mx-2 modal-add-btn" variant="" onClick={addDeliveryRate}>
                                Add
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>

                {/* Edit Modal */}
                <Modal show={showEditModal} onHide={handleCloseEdit} style={{ zIndex: "99999" }} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Delivery Rate</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Hub</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={`${hubName} (${hubId ? (hubId.length > 10 ? hubId.slice(-6) : hubId) : 'N/A'})`}
                                        disabled
                                    />
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="do-sear">
                                    <Form.Label>Door Delivery Available</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="door-delivery-edit-switch"
                                        label={doorDeliveryAvailable ? "Yes" : "No"}
                                        checked={doorDeliveryAvailable}
                                        onChange={(e) => {
                                            setDoorDeliveryAvailable(e.target.checked);
                                            if (!e.target.checked) {
                                                setDoorDeliveryRate("");
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {doorDeliveryAvailable && (
                                <div className="row">
                                    <div className="do-sear mt-2">
                                        <Form.Label>Door Delivery Rate (â‚¹)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="Enter Door Delivery Rate"
                                            value={doorDeliveryRate}
                                            onChange={(e) => setDoorDeliveryRate(e.target.value)}
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="row mt-3">
                                <div className="do-sear">
                                    <Form.Label>Gate Delivery Available (Free)</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="gate-delivery-edit-switch"
                                        label={gateDeliveryAvailable ? "Yes - Manage Pickup Points" : "No"}
                                        checked={gateDeliveryAvailable}
                                        onChange={(e) => {
                                            setGateDeliveryAvailable(e.target.checked);
                                            if (!e.target.checked) {
                                                setPickupPoints([]);
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        {statusOptions.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="d-flex">
                            <Button className="mx-2 modal-close-btn" variant="" onClick={handleCloseEdit}>
                                Close
                            </Button>
                            <Button className="mx-2 modal-add-btn" variant="" onClick={updateDeliveryRate}>
                                Update
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>

                {/* Pickup Points Modal */}
                <Modal show={showPickupPointsModal} onHide={handleClosePickupPoints} style={{ zIndex: "99999" }} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Pickup Points - {selectedRate?.hubName}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex justify-content-end mb-3">
                            <Button variant="success" onClick={handleShowAddPickupPoint}>
                                <BsPlus /> Add Pickup Point
                            </Button>
                        </div>
                        {pickupPoints.length > 0 ? (
                            <div className="row">
                                {pickupPoints.map((point) => (
                                    <div key={point._id} className="col-md-6 mb-3">
                                        <div className="border p-3 rounded">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div style={{ flex: 1 }}>
                                                    <h5>{point.name}</h5>
                                                    <p className="mb-1"><strong>Location:</strong> {point.location}</p>
                                                    {point.contactNumber && (
                                                        <p className="mb-1">
                                                            <strong>Contact:</strong>
                                                            <a href={`tel:${point.contactNumber}`} className="ms-2 text-decoration-none">
                                                                <BsTelephone className="me-1" /> {point.contactNumber}
                                                            </a>
                                                        </p>
                                                    )}
                                                    <p className="mb-1"><strong>Delivery Slots:</strong></p>
                                                    {point.deliverySlots?.map((slot, idx) => (
                                                        <div key={idx} className="ms-3 mb-2">
                                                            <strong>{slot.session}:</strong>
                                                            <ul className="mb-0">
                                                                {slot.timeSlots.map((ts, tIdx) => (
                                                                    <li key={tIdx}>{ts.startTime} - {ts.endTime}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                    {point.description && <p className="mb-0 text-muted mt-2">{point.description}</p>}
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="link"
                                                        className="text-primary"
                                                        onClick={() => handleShowEditPickupPoint(point)}
                                                    >
                                                        <BsPencil size={20} />
                                                    </Button>
                                                    <Button
                                                        variant="link"
                                                        className="text-danger"
                                                        onClick={() => removePickupPoint(point._id)}
                                                    >
                                                        <AiFillDelete size={20} />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <p className="text-muted">No pickup points available for gate delivery.</p>
                                <Button variant="success" onClick={handleShowAddPickupPoint}>
                                    Add First Pickup Point
                                </Button>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button className="modal-close-btn" onClick={handleClosePickupPoints}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Add Pickup Point Modal */}
                <Modal show={showAddPickupPointModal} onHide={handleCloseAddPickupPoint} style={{ zIndex: "999999" }} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Add Pickup Point</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Pickup Point Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., Block A, Security Gate, Main Entrance"
                                        value={pickupPointName}
                                        onChange={(e) => setPickupPointName(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Location *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., Near Block A entry point"
                                        value={pickupPointLocation}
                                        onChange={(e) => setPickupPointLocation(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Contact Number (Optional)</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        placeholder="e.g., +1234567890 or 1234567890"
                                        value={pickupPointContactNumber}
                                        onChange={(e) => setPickupPointContactNumber(e.target.value)}
                                    />
                                    <small className="text-muted">Contact number for pickup point inquiries</small>
                                </div>
                            </div>

                            {/* Delivery Slots Section */}
                            <div className="row mt-3">
                                <div className="do-sear">
                                    <Form.Label>Delivery Slots *</Form.Label>
                                    <div className="border p-3 rounded">
                                        {deliverySlots.map((slot, idx) => (
                                            <div key={idx} className="mb-3 p-2 border rounded">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <strong>{slot.session}</strong>
                                                    <Button
                                                        variant="link"
                                                        className="text-danger"
                                                        onClick={() => removeDeliverySlotSession(idx)}
                                                    >
                                                        <BsTrash />
                                                    </Button>
                                                </div>
                                                {slot.timeSlots.map((ts, tIdx) => (
                                                    <div key={tIdx} className="ms-3">
                                                        {ts.startTime} - {ts.endTime}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}

                                        <div className="mt-2">
                                            <Form.Label>Add New Session Slot</Form.Label>
                                            <div className="row g-2">
                                                <div className="col-md-3">
                                                    <Form.Select
                                                        value={currentSession}
                                                        onChange={(e) => setCurrentSession(e.target.value)}
                                                    >
                                                        {sessionOptions.map(opt => (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        ))}
                                                    </Form.Select>
                                                </div>
                                                <div className="col-md-8">
                                                    {timeSlots.map((ts, idx) => (
                                                        <div key={idx} className="d-flex gap-2 mb-2">
                                                            <Form.Control
                                                                type="time"
                                                                placeholder="Start Time"
                                                                value={ts.startTime}
                                                                onChange={(e) => updateTimeSlot(idx, "startTime", e.target.value)}
                                                            />
                                                            <Form.Control
                                                                type="time"
                                                                placeholder="End Time"
                                                                value={ts.endTime}
                                                                onChange={(e) => updateTimeSlot(idx, "endTime", e.target.value)}
                                                            />
                                                            {idx === timeSlots.length - 1 && (
                                                                <Button variant="outline-success" onClick={addTimeSlot}>
                                                                    <BsPlus />
                                                                </Button>
                                                            )}
                                                            {idx > 0 && (
                                                                <Button variant="outline-danger" onClick={() => removeTimeSlot(idx)}>
                                                                    <BsTrash />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="col-md-1">
                                                    <Button variant="primary" onClick={addDeliverySlotSession}>
                                                        Add
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Description (Optional)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Additional instructions or notes"
                                        value={pickupPointDescription}
                                        onChange={(e) => setPickupPointDescription(e.target.value)}
                                    />
                                </div>
                            </div>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="d-flex">
                            <Button className="mx-2 modal-close-btn" variant="" onClick={handleCloseAddPickupPoint}>
                                Cancel
                            </Button>
                            <Button
                                className="mx-2 modal-add-btn"
                                variant=""
                                onClick={addPickupPoint}
                                disabled={!pickupPointName || !pickupPointLocation || deliverySlots.length === 0}
                            >
                                Add Pickup Point
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>

                {/* Edit Pickup Point Modal */}
                <Modal show={showEditPickupPointModal} onHide={handleCloseEditPickupPoint} style={{ zIndex: "999999" }} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Pickup Point</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Pickup Point Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., Block A, Security Gate, Main Entrance"
                                        value={pickupPointName}
                                        onChange={(e) => setPickupPointName(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Location *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g., Near Block A entry point"
                                        value={pickupPointLocation}
                                        onChange={(e) => setPickupPointLocation(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Contact Number (Optional)</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        placeholder="e.g., +1234567890 or 1234567890"
                                        value={pickupPointContactNumber}
                                        onChange={(e) => setPickupPointContactNumber(e.target.value)}
                                    />
                                    <small className="text-muted">Contact number for pickup point inquiries</small>
                                </div>
                            </div>

                            {/* Delivery Slots Section */}
                            <div className="row mt-3">
                                <div className="do-sear">
                                    <Form.Label>Delivery Slots *</Form.Label>
                                    <div className="border p-3 rounded">
                                        {deliverySlots.map((slot, idx) => (
                                            <div key={idx} className="mb-3 p-2 border rounded">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <strong>{slot.session}</strong>
                                                    <Button
                                                        variant="link"
                                                        className="text-danger"
                                                        onClick={() => removeDeliverySlotSession(idx)}
                                                    >
                                                        <BsTrash />
                                                    </Button>
                                                </div>
                                                {slot.timeSlots.map((ts, tIdx) => (
                                                    <div key={tIdx} className="ms-3">
                                                        {ts.startTime} - {ts.endTime}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}

                                        <div className="mt-2">
                                            <Form.Label>Add New Session Slot</Form.Label>
                                            <div className="row g-2">
                                                <div className="col-md-3">
                                                    <Form.Select
                                                        value={currentSession}
                                                        onChange={(e) => setCurrentSession(e.target.value)}
                                                    >
                                                        {sessionOptions.map(opt => (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        ))}
                                                    </Form.Select>
                                                </div>
                                                <div className="col-md-8">
                                                    {timeSlots.map((ts, idx) => (
                                                        <div key={idx} className="d-flex gap-2 mb-2">
                                                            <Form.Control
                                                                type="time"
                                                                placeholder="Start Time"
                                                                value={ts.startTime}
                                                                onChange={(e) => updateTimeSlot(idx, "startTime", e.target.value)}
                                                            />
                                                            <Form.Control
                                                                type="time"
                                                                placeholder="End Time"
                                                                value={ts.endTime}
                                                                onChange={(e) => updateTimeSlot(idx, "endTime", e.target.value)}
                                                            />
                                                            {idx === timeSlots.length - 1 && (
                                                                <Button variant="outline-success" onClick={addTimeSlot}>
                                                                    <BsPlus />
                                                                </Button>
                                                            )}
                                                            {idx > 0 && (
                                                                <Button variant="outline-danger" onClick={() => removeTimeSlot(idx)}>
                                                                    <BsTrash />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="col-md-1">
                                                    <Button variant="primary" onClick={addDeliverySlotSession}>
                                                        Add
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="do-sear mt-2">
                                    <Form.Label>Description (Optional)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Additional instructions or notes"
                                        value={pickupPointDescription}
                                        onChange={(e) => setPickupPointDescription(e.target.value)}
                                    />
                                </div>
                            </div>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="d-flex">
                            <Button className="mx-2 modal-close-btn" variant="" onClick={handleCloseEditPickupPoint}>
                                Cancel
                            </Button>
                            <Button
                                className="mx-2 modal-add-btn"
                                variant=""
                                onClick={editPickupPoint}
                                disabled={!pickupPointName || !pickupPointLocation || deliverySlots.length === 0}
                            >
                                Update Pickup Point
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>

                {/* Delete Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseDelete} style={{ zIndex: "99999" }}>
                    <Modal.Header closeButton>
                        <Modal.Title>Warning</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row">
                            <div className="col-md-12">
                                <p className="fs-4" style={{ color: "red" }}>
                                    Are you sure?
                                    <br /> you want to delete this delivery rate for{" "}
                                    {selectedRate?.hubName}?
                                </p>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="" className="modal-close-btn" onClick={handleCloseDelete}>
                            Close
                        </Button>
                        <Button variant="" className="modal-add-btn" onClick={deleteDeliveryRate}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
}

export default DeliveryRate;